import { post, patch, put, del, get } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR, { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { handleApiResponse } from './api_util';
import { refetchCaseCategories } from './categories.js';
import { refetchCaseMerchants } from './merchants.js';
import { useContext } from 'react';
import UserProfileContext from '../context/userprofilecontext.js';
import { perfLog, perfNow } from '../utils/perfLog.js';
import { useGetAndStartCaseDatabaseStatus, isCaseDbReady } from './database_status.js';
import { segmentOf, segmentLabel } from '../utils/categoryOptions';
import { deriveCardholderName, deriveCardLabel } from '../utils/cardholder.js';

async function getTransactions() {

  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions'

  }

  const restOperation = get(payload);


  const response = await restOperation.response;

  return processResponse(response)
}


// Short-lived presigned URL for the check/deposit-slip crop folded onto a
// transaction (#6 transaction-detail thumbnail). Returns { url, doc_kind, page }
// or null when the transaction has no crop image. Fetched on demand (when the
// lightbox opens), not via SWR — the URL is short-lived by design.
export async function getCheckImageUrl(txnID) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: `transactions/check_image?txnID=${encodeURIComponent(txnID)}`,
  };
  const restOperation = get(payload);
  const response = await restOperation.response;
  return processResponse(response);
}


export function useGetTransactions(shouldFetch = true) {
  // Key MUST include caseID. Without it, SWR's cache is global and
  // switching between cases will display the previous case's transactions
  // until the new fetch resolves — alarming for a forensic-accounting tool
  // even though no real cross-tenant leak occurs.
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID ? ['TRANSACTIONS', caseID] : null,
    () => getTransactions()
  );
  return result;
}


// Flat mirror of the TRANSFER edge's human verdict, for the filter panel.
//
// DataFilter filters the ENRICHED rows (not the rendered list items), and its
// filter components key on a top-level primitive field — `transfer_partner`
// is nested, so it can't be filtered directly. This is the same reason
// `hasTransferPartner` exists. Non-transfer rows are false, so a "Proposed
// Transfers Only" switch narrows to exactly the pairs awaiting a verdict.
//
// Both legs of a pair share one TRANSFER edge, so both derive the same value
// and a filtered pair never renders half-orphaned.
//
// Exported because the optimistic review patches in transfer_review.js mutate
// `transfer_partner.disposition` directly and must re-derive this alongside it
// (see the sibling `hasTransferPartner` updates there).
export const deriveIsProposedTransfer = (transaction) =>
  transaction.transfer_partner?.disposition === 'PROPOSED';

// Flat "cross-ledger" flag for the filter panel (categorization segments,
// Phase 3): the row is categorized on a different ledger's chart than its
// account's ledger — personal spending inside a business account, or business
// activity through a personal account. Catches AI exceptions and human
// re-categorizations alike. Missing ledger/segment = Personal, matching the
// backend's coalesce(..., 'PERSONAL').
export const deriveIsCrossLedger = (transaction) =>
  segmentOf(transaction.subcategory) !== (transaction.bankAccount?.ledger_segment || 'PERSONAL');

// Flat "withheld flag" marker for the filter panel (contextual flag assessment
// §4.8): the row's flag edge is the stage's :SUPPRESSED one — the case flag
// assessment withheld this AI flag. Only rows from a feed read with
// include_withheld can ever carry one (the default read returns live edges
// only), and a restored flag's suppressed twin is shadowed server-side (live
// wins). Exported for the restore's optimistic patch, which must re-derive it
// alongside flag_status (same contract as the sibling derives above).
export const deriveIsWithheldFlag = (transaction) => transaction.flag_status === 'SUPPRESSED';

// Flat "restored flag" marker for the flag cell's Undo (t_892abb39): the row's
// human CONFIRMED verdict was a reviewer restore — the enriched read says so
// with flag_restored, computed from the coexisting :SUPPRESSED audit edge the
// restore never deletes (flag.py). That pairing IS the undo marker: an ordinary
// human CONFIRMED flag has no audit edge and never carries the key (the read
// emits it only when true). Exported because the cell gates its Undo on exactly
// this pairing at both call sites, and the optimistic patches must set/clear it
// alongside flag_status (same contract as deriveIsWithheldFlag above).
export const deriveIsRestoredFlag = (transaction) =>
  transaction.flag_status === 'CONFIRMED' && transaction.flag_restored === true;


export const mapTransactionToFilterableTransaction = (transaction) => {
  return {
    ...transaction,
    bankAccountName: transaction.bankAccount?._displayName || 'Unknown Account',
    transactionDate: transaction.transaction?.posting_date || 'Unknown Date',
    description: transaction.transaction?.description || 'No Description',
    amount: transaction.transaction?.amount || 0,
    merchantName: transaction.merchant?.name ,
    merchantLocation: transaction.merchantLocation?.name || 'Unknown Location',
    // Preserve the :Location (MERCHANT_LOCATION) geocode separately — the line
    // above overwrites the merchantLocation object with just its name, which
    // drops the place_id the transaction-detail map needs (e.g. a check payee
    // address that geocodes when the canonical :Merchant cannot).
    merchantLocationGeocode: transaction.merchantLocation?.geocode || null,
    associatedNames: transaction.associatedNames?.map(name => name.name) || [],
    tripLocations: transaction.tripLocations?.map(location => location.name) || [],
    subcategoryName: transaction.subcategory?.name || 'Uncategorized',
    categoryName: transaction.category?.name || 'Uncategorized',
    // Ledger the row is categorized on (Personal / Business / Investment chart).
    ledgerName: segmentLabel(segmentOf(transaction.subcategory)),
    flagName: transaction.flag?.name,
    notes: transaction.transaction?.notes || '',
    hasTransferPartner: transaction.transfer_partner !== null,
    isProposedTransfer: deriveIsProposedTransfer(transaction),
    isCrossLedger: deriveIsCrossLedger(transaction),
    isWithheldFlag: deriveIsWithheldFlag(transaction),
    // flag_status / flag_assessment ride the `...transaction` spread: the
    // enriched read projects them (§4.8) and the flag cell reads them there.
    cardholderName: deriveCardholderName(transaction.card_view),
    cardLabel: deriveCardLabel(transaction.card_view),
  };
}

async function getEnrichedTransactionsByIDList(txnIDList) {
  console.log("getEnrichedTransactionsByIDList called with txnIDList:", txnIDList);
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/enriched',
    options: {
      body: {
        txnIDList: txnIDList,
      }
    }

  }

  const restOperation = post(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  // console.log("processResponse completed with transactions:", transactions);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);
  // console.log("getEnrichedTransactionsByIDList completed with transactions:", filterableTransactions);

  return filterableTransactions
}


export function useGetEnrichedTransactionsByIDList(txnIDList, shouldFetch = true) {
  // Scope by caseID so a case switch can't reveal cached transactions from
  // the previous case (see comment on useGetTransactions).
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  const result = useSWR(
    shouldFetch && caseID ? ['ENRICHED_TRANSACTIONS_BY_ID', caseID, txnIDList] : null,
    () => getEnrichedTransactionsByIDList(txnIDList)
  );
  return result;
}

// Paginated by-ID enriched-transactions fetch.
//
// Why this exists alongside the plain useGetEnrichedTransactionsByIDList: a
// single POST returning all enriched rows for an arbitrary ID list will hit
// the API Gateway ~10MB sync ceiling somewhere around 4-6K rows (each
// enriched record carries nested merchant/location/category/flag/transfer
// objects ≈ 1-2KB). Dashboard drilldowns on poorly-categorized cases easily
// produce ID lists of that size.
//
// We slice the ID list CLIENT-SIDE — each page POSTs only its own slice of
// IDs and gets back only those rows. No backend skip/limit is used; the
// backend handler is unchanged. Pages are wrapped as {data: [...]} so the
// cache shape matches useGetEnrichedTransactions (the full-feed) — this is
// what lets the existing edit hooks (useUpdateCategoryForTransactions etc.)
// mutate either source through the same Page[] code path with no adapter.
export function useGetEnrichedTransactionsByIDListInfinite(txnIDList, shouldFetch = true, limit = 4000) {
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;
  const idList = Array.isArray(txnIDList) ? txnIDList : [];

  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && previousPageData.data && previousPageData.data.length === 0) return null;
    if (pageIndex * limit >= idList.length) return null;
    if (!shouldFetch || !caseID) return null;
    return ['ENRICHED_TRANSACTIONS_BY_ID_INFINITE', caseID, pageIndex, limit, idList];
  };

  const fetcher = async (key) => {
    const pageIndex = key[2];
    const pageLimit = key[3];
    const ids = key[4];
    const slice = ids.slice(pageIndex * pageLimit, (pageIndex + 1) * pageLimit);
    if (slice.length === 0) return { data: [] };
    const transactions = await getEnrichedTransactionsByIDList(slice);
    return { data: transactions };
  };

  // Auto-fetch all pages on mount so the user lands on the full drilldown
  // result, not a partial "first 4000". The initialSize ensures useSWRInfinite
  // requests every page in parallel up front; persistSize keeps it from
  // collapsing back on revalidation.
  const totalPages = Math.max(1, Math.ceil(idList.length / limit));
  const options = {
    initialSize: totalPages,
    persistSize: true,
    revalidateFirstPage: false,
  };

  return useSWRInfinite(getKey, fetcher, options);
}

async function getEnrichedTransactions(pageIndex = null, limit = 4000, includeWithheld = false) {
  const skip = (pageIndex??0) * limit;
  const _t0 = perfNow();

  const payload = {
    apiName: 'LivisiblyTransactionApi',
    // include_withheld opts this read into the flag assessment's SUPPRESSED
    // rows (§4.8). Omitted entirely when off: the default feed stays
    // byte-identical to before, including the piggyback live total the
    // parallel burst below relies on (the server withholds it on
    // include_withheld reads by design).
    path: `transactions/enriched${pageIndex ? `?skip=${skip}&limit=${limit}` : `?limit=${limit}`}${includeWithheld ? '&include_withheld=true' : ''}`,
  }

  const restOperation = get(payload);


  const response = await restOperation.response;

  const _tResp = perfNow();
  const rawResult = await processResponse(response);
  const filterableTransactions = rawResult.data.map(mapTransactionToFilterableTransaction);
  rawResult.data = filterableTransactions;
  // Primitives only. `startTs` (relative ms) lets us see whether pages overlap
  // (parallel) or run back-to-back (sequential); `netMs` is network+response,
  // `totalMs` adds JSON parse + the filterable map.
  perfLog('fetch.enriched.page', {
    page: pageIndex ?? 0,
    rows: filterableTransactions.length,
    netMs: +(_tResp - _t0).toFixed(1),
    totalMs: +(perfNow() - _t0).toFixed(1),
    startTs: +_t0.toFixed(1),
  });
  return rawResult;


}

export function flattenInfiniteData(data) {
  if (!data) return [];
  return data.reduce((all, page) => {
    if (page && page.data) {
      return all.concat(page.data);
    }
    return all;
  }, []);
}

export function getOptimisticInfiniteData(data, updateFunction) {
  // data is array of pages
  // console.log("TEST1 getOptimisticInfiniteData called", data);
  if (!data) return [];
  const result = data.map((page) => {
    if (page && page.data) {
      const updatedData = updateFunction(page.data);
      return {
        ...page,
        data: updatedData
      };
    }
    return page;
  });
  // console.log("TEST1 getOptimisticInfiniteData result", result);
  return result;
}

function updateInfiniteDataFromNewList(updatedData, data){
  // for each transaction in updatedData, find it in data and replace it.
  // Build the id -> updated transaction lookup ONCE so the per-row replace is
  // O(1) instead of updatedData.find() (O(n)) per row — on a 31k dataset the
  // .find-per-row was O(n²) and allocated a fresh array per comparison.
  console.log("TEST1 updateInfiniteDataFromNewList called", {updatedData, data});
  const updatedById = new Map();
  for (const t of updatedData) {
    if (t && t.transaction && t.transaction.id != null) {
      updatedById.set(t.transaction.id, t);
    }
  }
  const result = data.map((page) => {
    if (page && page.data) {
      const newPageData = page.data.map((transaction) => {
        const updatedTransaction = updatedById.get(transaction.transaction.id);
        return updatedTransaction || transaction;
      });
      const newPageData2 = newPageData.map((transaction) => {
        if (transaction.transfer_partner) {
          const updatedTransferPartner = updatedById.get(transaction.transfer_partner.transaction.id);
          if (updatedTransferPartner) {
            return {
              ...transaction,
              transfer_partner: updatedTransferPartner
            };
          }
        }
        return transaction;
      });
      return {
        ...page,
        data: newPageData2
      };
    }
    return page;
  });
  console.log("TEST1 updateInfiniteDataFromNewList result", result);
  return result;
}

// Full-feed page size — 4000 rows/page (prod value). Sized to keep a single
// page's enriched response under the ~6MB Lambda sync-response cap (each enriched
// row is ~1-2KB nested), so a ~40k-row case is ~10 pages fetched in one parallel
// wave (~16s). Was temporarily 1000 for multi-page dev testing.
export const ENRICHED_PAGE_LIMIT = 4000;

// Max concurrent page fetches. At prod page size a ~40k-row case is ~10 pages,
// so 10 fetches the whole remainder in a single wave (~16s vs ~24s at 6). The
// per-case Neo4j boxes are amply provisioned and see low concurrency, so the
// burst is acceptable; tune down if a case DB ever shows contention.
const ENRICHED_FETCH_CONCURRENCY = 10;

// Bounded-parallel map that preserves input order. Runs at most `limit` of `fn`
// concurrently; each worker pulls the next index until the list is exhausted.
export async function mapWithConcurrency(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const idx = next++;
      out[idx] = await fn(items[idx], idx);
    }
  });
  await Promise.all(workers);
  return out;
}

// Piggyback + parallel full-feed fetch.
//
// Page 0 (skip 0) carries a LIVE `total` from the backend (a count-store-cheap
// join count — measured ~free on prod), so we learn the page count from data we
// were already fetching, with NO separate count round trip. We then fetch pages
// 1..N-1 in a bounded-parallel burst. useSWRInfinite can't express this (its
// parallel mode gives getKey no previousPageData, so it can't read page 0's
// total), which is why the hook below is a plain useSWR over this orchestrator.
//
// Returns Page[] ([{data:[...]}, ...]) — the SAME shape useSWRInfinite produced
// — so flattenInfiniteData and the optimistic-edit helpers
// (getOptimisticInfiniteData / updateInfiniteDataFromNewList) are unchanged.
async function getEnrichedFeedParallel(pageLimit, includeWithheld = false, maxConcurrent = ENRICHED_FETCH_CONCURRENCY) {
  const page0 = await getEnrichedTransactions(0, pageLimit, includeWithheld); // { data:[...], total?:N }
  const pages = [page0];
  const total = Number(page0?.total);
  const t0 = perfNow();

  if (Number.isFinite(total) && total > pageLimit) {
    const lastIndex = Math.ceil(total / pageLimit) - 1; // pages 1..lastIndex
    const indices = [];
    for (let i = 1; i <= lastIndex; i++) indices.push(i);
    const rest = await mapWithConcurrency(indices, maxConcurrent, (i) => getEnrichedTransactions(i, pageLimit, includeWithheld));
    pages.push(...rest);
  }

  // Safety tail: a row inserted mid-fetch (active upload) — or a missing/low
  // `total` — leaves the last page FULL, meaning there may be more. Keep
  // fetching sequentially until a short/empty page. The live count makes this
  // the rare exception, not the common path. With include_withheld the walk
  // must not stop at a SHORT page: the live-wins shadow-drop of a restored
  // flag's suppressed twin happens server-side after row building, so a page
  // can come back short while rows remain (skip/limit count raw edges). Only
  // an EMPTY window ends the feed there. Default (live) mode is unchanged.
  const morePages = (lastPage) => includeWithheld
    ? (lastPage?.data?.length ?? 0) > 0
    : lastPage?.data?.length === pageLimit;
  while (morePages(pages[pages.length - 1])) {
    const p = await getEnrichedTransactions(pages.length, pageLimit, includeWithheld);
    if (!p?.data?.length) break;
    pages.push(p);
  }

  perfLog('feed.parallelFetch', {
    pages: pages.length,
    total: Number.isFinite(total) ? total : -1,
    concurrency: maxConcurrent,
    remainderMs: +(perfNow() - t0).toFixed(1),
  });
  return pages;
}

export function useGetEnrichedTransactions(shouldFetch = true, limit = ENRICHED_PAGE_LIMIT, includeWithheld = false) {
  // Scope the cache key by caseID so switching cases never returns the
  // previous case's transactions from the SWR cache. Without this, the
  // Sankey/transaction views show the prior case's data for the duration
  // of the network request after a case switch.
  const userProfileContext = useContext(UserProfileContext);
  const caseID = userProfileContext?.userProfile?.currentCase?.caseID || null;

  // The withheld-flag opt-in (§4.8) is part of the key: the live feed and the
  // feed that additionally carries the stage's SUPPRESSED rows are different
  // reads and must never share a cache entry. Every existing caller lands on
  // `false` — the same data as before, one extra key element.
  // Single useSWR over the piggyback+parallel orchestrator (see above). The
  // cached value is Page[], so mutate/optimistic edits work exactly as they did
  // under useSWRInfinite.
  return useSWR(
    shouldFetch && caseID ? ['ENRICHED_TRANSACTIONS', caseID, limit, includeWithheld === true] : null,
    ([, , lim, withheld]) => getEnrichedFeedParallel(lim, withheld),
    { revalidateOnFocus: false }
  );
}

export function EnrichedTransactionPreloader(shouldFetch = true) {
  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext.userProfile;
  const isActiveCase = userProfile?.currentCase?.status === 'ACTIVE';

  // Wait for the case DB to actually be reachable before firing the real
  // feed query. On a case that hasn't been opened in a while, the Neo4j EC2
  // instance is stopped and can take up to ~90s+ to become query-ready
  // (case-db-ec2-lifecycle.md); the query lambda's own retry budget for that
  // (~115s, dbutils.py) is well past API Gateway's hard 29s integration
  // timeout. Firing the feed query immediately on case-activation — as this
  // preloader used to — raced that cold start and burned through several
  // guaranteed-504 attempts (each a real, several-minute Lambda invocation
  // that the browser had already given up on) before one finally landed
  // inside the 29s window. Piggybacking on the same DB-status poll
  // CaseDatabaseCheck uses (shared SWR key) means the preloader still kicks
  // off the lazy EC2 start immediately, it just holds the expensive query
  // until the DB is confirmed up.
  const shouldCheckDb = shouldFetch && isActiveCase;
  const dbStatus = useGetAndStartCaseDatabaseStatus(shouldCheckDb);
  const isDbReady = isCaseDbReady(dbStatus.data?.status);

  const shouldFetchEnrichedTransactions = shouldCheckDb && isDbReady;
  useGetEnrichedTransactions(shouldFetchEnrichedTransactions);
  return null;

}

// `segment` is the ledger segment (PERSONAL / BUSINESS / INVESTMENT chart) of
// the chosen subcategory; omitted → the backend uses PERSONAL.
async function updateCategoryForTransactions(txnIDList, categoryType, categoryName, subcategoryName, confidenceLevel, rationale, predictionModel, segment) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/category',
    options: {
      body: {
        txnIDList: txnIDList,
        category: {
          type: categoryType,
          category: categoryName,
          subcategory: subcategoryName,
          rationale: rationale,
          confidence_level: confidenceLevel,
          prediction_model: predictionModel,
          ...(segment ? { segment } : {})
        }
      }
    }
  }
  const restOperation = patch(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseCategories();

  return filterableTransactions
}



export function useUpdateCategoryForTransactions(mutateTransactions) {
  return (txnIDList, categoryType, categoryName, subcategoryName, confidenceLevel, rationale, predictionModel, segment) => {
    const resolvedSegment = segment || 'PERSONAL';
    function optimisticUpdateCategoryForTransactions(enrichedTransactions) {
      // Set lookups instead of txnIDList.includes() per row — includes is
      // O(k), so scanning all rows made a bulk edit O(n·k); on a 31k dataset
      // with a large selection that's the dominant cost. O(1) per row here.
      const txnIDSet = new Set(txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          const newEnrichedTransaction = {
            ...enrichedTransaction,
            category: {
              type: categoryType,
              name: categoryName,
              segment: resolvedSegment
            },
            subcategory: {
              name: subcategoryName,
              type: categoryType,
              segment: resolvedSegment
            },
            category_rationale: {
              confidence_level: confidenceLevel,
              matched_basis: rationale,
              prediction_model: predictionModel
            }
          }
          // The flat filter fields read the new chart, so a re-categorized row
          // moves in or out of the Ledger / Cross-ledger filters immediately.
          newEnrichedTransaction.ledgerName = segmentLabel(resolvedSegment);
          newEnrichedTransaction.isCrossLedger = deriveIsCrossLedger(newEnrichedTransaction);
          return newEnrichedTransaction
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              category: {
                type: categoryType,
                name: categoryName,
                segment: resolvedSegment
              },
              subcategory: {
                name: subcategoryName,
                type: categoryType,
                segment: resolvedSegment
              },
              category_rationale: {
                confidence_level: confidenceLevel,
                matched_basis: rationale,
                prediction_model: predictionModel
              }
            }
          }
        }
        return enrichedTransaction;
      });

    }
    // const optimisticUpdate = data => {
    //   console.log("TEST1 optimisticUpdate called", data);
    //   return data
    // }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateCategoryForTransactions),
      // optimisticData: optimisticUpdate,
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateCategoryForTransactions(txnIDList, categoryType, categoryName, subcategoryName, confidenceLevel, rationale, predictionModel, segment),
      options);
  }
}

async function updateMerchantForTransactions(txnIDList, merchantData) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/merchant',
    options: {
      body: {
        txnIDList: txnIDList,
        merchant: merchantData
      }
    }
  }
  const restOperation = patch(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseMerchants();

  return filterableTransactions
}

// Apply only the merchant fields present in the payload. An absent field means
// "leave unchanged", mirroring the server's partial-update semantics, so an
// untouched field is not optimistically blanked in the UI either.
const MERCHANT_PAYLOAD_KEY_TO_FIELD = {
  merchant_id: 'id',
  merchant_name: 'name',
  merchant_website: 'website',
  merchant_phone: 'phone',
  merchant_street_address: 'street_address',
  merchant_city: 'city',
  merchant_state: 'state',
  merchant_zip: 'zip'
};

// An empty merchant_name is the unlink signal, not a rename: the server drops
// the :MERCHANT and :MERCHANT_LOCATION relationships outright. Merging name:''
// onto the existing object would leave the old website/city rendering under a
// blank name until revalidation, so null the whole thing instead.
const isMerchantRemoval = (merchantData) => merchantData?.merchant_name === '';

// Cached rows are mapTransactionToFilterableTransaction output, so they carry
// derived merchant keys next to the nested objects. Clearing only the nested
// `merchant` would leave the flattened name/location/geocode driving the row
// and the detail map. These values mirror exactly what the mapper produces for
// a transaction with no merchant and no location.
const REMOVED_MERCHANT_FIELDS = {
  merchant: null,
  merchantLocation: 'Unknown Location',
  merchantLocationGeocode: null,
  merchantName: undefined
};

function applyMerchantPatch(existingMerchant, merchantData) {
  if (isMerchantRemoval(merchantData)) {
    return null;
  }

  return Object.entries(MERCHANT_PAYLOAD_KEY_TO_FIELD).reduce((merchant, [key, field]) => {
    if (merchantData?.[key] !== undefined) {
      merchant[field] = merchantData[key];
    }
    return merchant;
  }, { ...existingMerchant });
}

export function useUpdateMerchantForTransactions(mutateTransactions) {
  return (txnIDList, merchantData) => {
    function optimisticUpdateMerchantForTransactions(enrichedTransactions) {
      // Set lookups (see optimisticUpdateCategoryForTransactions) — O(1)/row
      // instead of O(k) includes per row on a bulk edit.
      const txnIDSet = new Set(txnIDList);
      console.log("optimisticUpdateMerchantForTransactions called with txnIDList:", txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          console.log(`found transaction with id=${enrichedTransaction.transaction.id}`)
          console.log("old enriched Transaction")
          console.log(enrichedTransaction)
          const newEnrichedTransaction = isMerchantRemoval(merchantData)
            ? { ...enrichedTransaction, ...REMOVED_MERCHANT_FIELDS }
            : {
              ...enrichedTransaction,
              merchant: applyMerchantPatch(enrichedTransaction.merchant, merchantData)
            }
          console.log("new enriched Transaction")
          console.log(newEnrichedTransaction)
          return newEnrichedTransaction
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: isMerchantRemoval(merchantData)
              ? { ...enrichedTransaction.transfer_partner, ...REMOVED_MERCHANT_FIELDS }
              : {
                ...enrichedTransaction.transfer_partner,
                merchant: applyMerchantPatch(enrichedTransaction.transfer_partner.merchant, merchantData)
              }
          }
        }
        return enrichedTransaction;
      });

    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateMerchantForTransactions),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateMerchantForTransactions(txnIDList, merchantData),
      options);

  }
}


// --- Free-form user note on a transaction (t.notes + attribution) ---
// A note is a direct property on the :Transaction node, so it round-trips as
// transaction.transaction.notes with no read-path changes. Empty string clears.

async function updateNotesForTransactions(txnIDList, notes) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/notes',
    options: {
      body: {
        txnIDList: txnIDList,
        notes: notes
      }
    }
  }
  const restOperation = patch(payload);

  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  return filterableTransactions
}

export function useUpdateNotesForTransactions(mutateTransactions) {
  return (txnIDList, notes) => {
    function optimisticUpdateNotesForTransactions(enrichedTransactions) {
      // Set lookups (see optimisticUpdateCategoryForTransactions) — O(1)/row
      // instead of O(k) includes per row on a bulk edit.
      const txnIDSet = new Set(txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          // Optimistically show the new note text; server response fills in the
          // authoritative attribution (notesUpdatedBy / notesUpdatedAt).
          return {
            ...enrichedTransaction,
            transaction: {
              ...enrichedTransaction.transaction,
              notes: notes,
            }
          }
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              transaction: {
                ...enrichedTransaction.transfer_partner.transaction,
                notes: notes,
              }
            }
          }
        }
        return enrichedTransaction;
      });
    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateNotesForTransactions),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateNotesForTransactions(txnIDList, notes),
      options);

  }
}


// --- User correction of a scanned description (OCR misread) ---
// The server keeps the scanned text in description_original and the user's text
// in description_edited (both present only while edited), and re-enriches the
// transaction. Empty description (or one equal to the scan) reverts.

async function updateDescriptionForTransaction(txnID, description) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/description',
    options: {
      body: {
        txnID: txnID,
        description: description
      }
    }
  }
  const restOperation = patch(payload);

  const response = await restOperation.response;
  const transactions = await processResponse(response);
  return transactions.map(mapTransactionToFilterableTransaction);
}

// Optimistic mirror of set_transaction_description's edit/revert rule. The
// "original" is always the OCR-published text, never a previous human edit.
export function applyDescriptionEdit(txn, description) {
  const original = txn.description_edited != null
    ? (txn.description_original ?? null)
    : txn.description;
  const text = (description || '').trim();
  if (!text || text === original) {
    return {
      ...txn,
      description: original,
      description_original: null,
      description_edited: null,
      description_editedBy: null,
      description_editedByUserId: null,
      description_editedAt: null,
    };
  }
  return { ...txn, description: text, description_original: original, description_edited: text };
}

export function useUpdateDescriptionForTransaction(mutateTransactions) {
  return (txnID, description) => {
    function optimisticUpdateDescription(enrichedTransactions) {
      return enrichedTransactions.map(enrichedTransaction => {
        if (enrichedTransaction.transaction.id === txnID) {
          return { ...enrichedTransaction, transaction: applyDescriptionEdit(enrichedTransaction.transaction, description) };
        }
        if (enrichedTransaction.transfer_partner?.transaction?.id === txnID) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              transaction: applyDescriptionEdit(enrichedTransaction.transfer_partner.transaction, description),
            }
          }
        }
        return enrichedTransaction;
      });
    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateDescription),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    // Returned so the caller can surface a failure (the optimistic edit rolls back).
    return mutateTransactions(updateDescriptionForTransaction(txnID, description), options);
  }
}


async function updateFlagForTransactions(txnIDList, flagData) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/flag',
    options: {
      body: {
        txnIDList: txnIDList,
        flag: {
          name: flagData.flag,
          rationale: flagData.rationale,
          confidence_level: flagData.confidenceLevel,
          prediction_model: flagData.predictionModel
        }
      }
    }
  }
  const restOperation = patch(payload);


  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseMerchants();

  return filterableTransactions
}

export function useUpdateFlagForTransactions(mutateTransactions) {
  return (txnIDList, flagData) => {
    function optimisticUpdateFlagForTransactions(enrichedTransactions) {
      // Set lookups (see optimisticUpdateCategoryForTransactions) — O(1)/row
      // instead of O(k) includes per row on a bulk edit.
      const txnIDSet = new Set(txnIDList);
      // console.log("optimisticUpdateFlagForTransactions called with txnIDList:", txnIDList);
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          // console.log(`found transaction with id=${enrichedTransaction.transaction.id}`)
          // console.log("old enriched Transaction")
          // console.log(enrichedTransaction)
          const newEnrichedTransaction = {
            ...enrichedTransaction,
            flag: {
              ...enrichedTransaction.flag,
              name: flagData.flag,
            },
            flag_basis: {
              ...enrichedTransaction.flag_basis,
              confidence_level: flagData.confidenceLevel,
              matched_basis: flagData.rationale,
              prediction_model: flagData.predictionModel

            }
          }
            // console.log("new enriched Transaction")
            // console.log(newEnrichedTransaction)
          return newEnrichedTransaction
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: {
              ...enrichedTransaction.transfer_partner,
              flag: {
                ...enrichedTransaction.transfer_partner.flag,
                name: flagData.flag,
              },
              flag_basis: {
                ...enrichedTransaction.transfer_partner.flag_basis,
                confidence_level: flagData.confidenceLevel,
                matched_basis: flagData.rationale,
                prediction_model: flagData.predictionModel

              }
            }
          }
        }
        return enrichedTransaction;
      });

    }

    const options = {
      optimisticData: data => getOptimisticInfiniteData(data, optimisticUpdateFlagForTransactions),
      revalidate: false,
      populateCache: updateInfiniteDataFromNewList,
      rollbackOnError: true
    }

    mutateTransactions(
      updateFlagForTransactions(txnIDList, flagData),
      options);

  }
}


// Reviewer restore of a withheld flag (contextual flag assessment §4.6): the
// flag becomes a HUMAN verdict, final. PATCH /transactions/flag/restore with
// the sibling flag route's body shape — {txnIDList, flag: {name,
// assessmentID?}}; the assessment id pins WHICH stage suppression is being
// restored (it is always present on a withheld row; omitted = the server
// restores whatever suppression stands). Response: the enriched read of the
// restored ids — NOT patched into the cache row-by-row: the flag actions
// settle by revalidating the key (see the options below, t_4553bc0c).
async function restoreFlagForTransactions(txnIDList, flagData) {
  const flag = { name: flagData.name };
  if (flagData.assessmentID) {
    flag.assessmentID = flagData.assessmentID;
  }
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/flag/restore',
    options: {
      body: {
        txnIDList: txnIDList,
        flag: flag,
      }
    }
  }
  const restOperation = patch(payload);

  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseMerchants();

  return filterableTransactions
}

export function useRestoreFlagForTransactions(mutateTransactions) {
  return (txnIDList, flagData) => {
    // The optimistic shape mirrors exactly what flag.py's restore writes
    // server-side: the human CONFIRMED edge (Manually Assigned / Assigned) and
    // NO assessment — rule 2, a human verdict is never assessed. The row stops
    // being a withheld one at once (the §4.8 filter drops it, the flag cell's
    // "Withheld because" line disappears with the assessment); the server's
    // enriched read then settles every field authoritatively.
    function optimisticRestoreForTransactions(enrichedTransactions) {
      const txnIDSet = new Set(txnIDList);
      const asHumanVerdict = (row) => ({
        ...row,
        flag_status: 'CONFIRMED',
        flag_assessment: null,
        // The restore keeps the SUPPRESSED audit edge beside the new verdict —
        // that pairing is exactly what the server's read answers with
        // (flag_restored), so the cell's Undo lands with the row at once.
        flag_restored: true,
        flag_rationale: {
          ...row.flag_rationale,
          prediction_model: 'Manually Assigned',
          confidence_level: 'Assigned',
        },
        isWithheldFlag: deriveIsWithheldFlag({ ...row, flag_status: 'CONFIRMED' }),
      });
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          return asHumanVerdict(enrichedTransaction);
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: asHumanVerdict(enrichedTransaction.transfer_partner),
          };
        }
        return enrichedTransaction;
      });
    }

    const options = {
      // t_4553bc0c fix 2 — the optimistic write must be a functional update
      // against the CURRENT cache. SWR 2.2.5's internalMutate calls
      // optimisticData(committedData, displayedData) (dist/_internal/index.js
      // :303-310): while a second flag mutation overlaps the first, the first
      // argument is the `_c` backup — the list as it stood BEFORE the first
      // mutation's optimistic write — so patching from it reverts the sibling
      // row. That is the reported defect: after two quick restores the table
      // still showed one row withheld while a reload showed the withheld view
      // empty. The second argument is the current displayed value; building
      // from it keeps an in-flight sibling's change.
      optimisticData: (committed, displayed) =>
        getOptimisticInfiniteData(displayed ?? committed, optimisticRestoreForTransactions),
      // The settle side of the same race cannot be fixed by patching rows:
      // SWR drops the non-last-started mutation's response outright (the
      // timestamp guard at :337-340 returns before any cache write), and
      // populateCache's second argument is that same captured committed
      // snapshot (:355) — no merge over a captured list can keep a sibling's
      // removal. So settle by revalidating the key: the cache is replaced by
      // the server's own read, and a row the server restored cannot be
      // resurrected client-side. The ~6s mutation round trip already gates
      // the action, so the refetch lands behind the click, not in front of
      // it (the card's ruling: correctness first). Same fire-then-revalidate
      // shape as transfer_review.js's relink and entities.js; a failed
      // action's rollback (rollbackOnError) is corrected by the same path.
      populateCache: false,
      revalidate: true,
      rollbackOnError: true
    }

    // Returned so the caller can surface success/failure (the transfer-review
    // actions in this view follow the same .then/.catch idiom).
    return mutateTransactions(
      restoreFlagForTransactions(txnIDList, flagData),
      options);
  }
}


// Undo of a reviewer restore (t_892abb39): the misclick fix. PATCH
// /transactions/flag/undo-restore with the restore route's body shape minus
// the assessment pin — {txnIDList, flag: {name}}. Server-side the call
// deletes ONLY the CONFIRMED verdict of a row whose SUPPRESSED audit edge
// still stands (the pairing can only be a restore; an ordinary human flag is
// never touched), so the row falls back to withheld with its reason intact —
// nothing else changes. includeWithheld is the CALLER's view, not a graph
// option: the post-restore confirmation acts from the withheld view and asks
// for the enriched read in the withheld shape (the row reappears beside its
// reason); the flag cell acts from the live feed, where the default read is
// the right shape (the flag stops being live, the row simply loses it).
async function undoRestoreFlagForTransactions(txnIDList, flagData, includeWithheld = false) {
  const payload = {
    apiName: 'LivisiblyTransactionApi',
    path: `transactions/flag/undo-restore${includeWithheld ? '?include_withheld=true' : ''}`,
    options: {
      body: {
        txnIDList: txnIDList,
        flag: { name: flagData.name },
      }
    }
  }
  const restOperation = patch(payload);

  const response = await restOperation.response;
  const transactions = await processResponse(response);
  const filterableTransactions = transactions.map(mapTransactionToFilterableTransaction);

  refetchCaseMerchants();

  return filterableTransactions
}

export function useUndoRestoreFlagForTransactions(mutateTransactions) {
  return (txnIDList, flagData, { includeWithheld = false, rowSnapshot = null } = {}) => {
    // The optimistic shape mirrors what the undo leaves behind: the audit edge
    // and its assessment stand again, the human verdict is gone. From the
    // withheld view that means the row re-enters the filtered table at once;
    // from the live feed it means the flag simply stops showing.
    //
    // t_eb5adf39 item 0: the post-restore confirmation's Undo also carries
    // rowSnapshot — the complete row exactly as it stood when Restore was
    // clicked. The cached row has since lost flag + flag_assessment to the
    // restore's own optimistic shape (rule 2), so synthesising from it put a
    // bare, half-height row back into the withheld view for the ~2s until the
    // server read landed — the table visibly broke mid-action. A snapshot is
    // not an invention: it is the row the client genuinely held, the same
    // shape the server read will settle, so with one the optimistic update
    // writes it back VERBATIM and the re-entered row is complete at t+0.
    // Without one (the flag cell's own Undo has no pre-restore row to hand)
    // the synthesised shape stands: there the reason never crossed to the
    // client, so it still rides in with the server's read. The live-feed
    // branch has no such gap — its synthesised shape already equals the
    // post-undo live row — so a snapshot is honoured only from the withheld
    // view, which is the only caller that can supply one.
    const snapshotId = includeWithheld ? rowSnapshot?.transaction?.id ?? null : null;
    function optimisticUndoForTransactions(enrichedTransactions) {
      const txnIDSet = new Set(txnIDList);
      const asWithheldAgain = (row) => (snapshotId && row.transaction?.id === snapshotId)
        ? rowSnapshot
        : includeWithheld
        ? {
            ...row,
            flag_status: 'SUPPRESSED',
            flag_restored: false,
            isWithheldFlag: true,
          }
        : {
            ...row,
            flag: null,
            flagName: undefined,
            flag_status: null,
            flag_assessment: null,
            flag_restored: false,
            isWithheldFlag: false,
          };
      return enrichedTransactions.map(enrichedTransaction => {
        if (txnIDSet.has(enrichedTransaction.transaction.id)) {
          return asWithheldAgain(enrichedTransaction);
        }
        if (enrichedTransaction.transfer_partner && txnIDSet.has(enrichedTransaction.transfer_partner.transaction.id)) {
          return {
            ...enrichedTransaction,
            transfer_partner: asWithheldAgain(enrichedTransaction.transfer_partner),
          };
        }
        return enrichedTransaction;
      });
    }

    const options = {
      // t_4553bc0c fix 2 — the same order-safe shape as the restore hook
      // above, for the same reason: SWR hands optimisticData the pre-sibling
      // `_c` snapshot as arg 1 (dist/_internal/index.js:303-310), and a write
      // built from it reverts an in-flight sibling mutation's row; arg 2 is
      // the current displayed value. Settle revalidates the key rather than
      // merging the response into the captured snapshot (:337-340, :355).
      optimisticData: (committed, displayed) =>
        getOptimisticInfiniteData(displayed ?? committed, optimisticUndoForTransactions),
      populateCache: false,
      revalidate: true,
      rollbackOnError: true
    }

    // Returned so the caller can surface success/failure (same idiom as the
    // restore and the transfer-review actions).
    return mutateTransactions(
      undoRestoreFlagForTransactions(txnIDList, flagData, includeWithheld),
      options);
  }
}


// --- Account-ownership Phase 2: per-transaction source-of-funds tagging ---
// (:Transaction)-[:OWNED_BY]->(:Entity). owners use entity_id + a 0-1 pct. The
// untagged remainder is NOT sourced here — that's the Phase 3 source calculation.

const transactionSourceKey = (txnID) => ['TRANSACTION_SOURCE', txnID];

async function getTransactionSource(txnID) {
  const restOperation = get({
    apiName: 'LivisiblyTransactionApi',
    path: `transactions/source?txnID=${encodeURIComponent(txnID)}`,
  });
  const response = await restOperation.response;
  return processResponse(response);
}

// Read a transaction's explicit source tags + untagged remainder. The editor
// loads this on open; returns {found, owners:[{entity_id, display_name, kind,
// pct, value_override}], tagged_pct, untagged_pct}.
export function useGetTransactionSource(txnID, shouldFetch = true) {
  return useSWR(
    shouldFetch && txnID ? transactionSourceKey(txnID) : null,
    ([, id]) => getTransactionSource(id),
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );
}

async function setTransactionSource(txnID, owners) {
  const restOperation = patch({
    apiName: 'LivisiblyTransactionApi',
    path: 'transactions/source',
    options: { body: { txnID, owners } },
  });
  const response = await restOperation.response;
  return processResponse(response);
}

// Replace a transaction's source tags. owners: [{entity_id, pct (0-1),
// value_override?, notes?}]; empty clears. Writes the returned source into the
// cache so the editor reflects the saved state without a refetch.
export function useSetTransactionSource() {
  return (txnID, owners) => mutate(
    transactionSourceKey(txnID),
    setTransactionSource(txnID, owners),
    { revalidate: false, populateCache: true, rollbackOnError: true },
  );
}